import {
  type FunctionComponent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';

import './style.scss';

/** Tuple of the numeric stop value and the corresponding label. */
type StopT = [number, string];

type PropsT = {
  light?: boolean;
  onChange: (value: number) => void;
  stops: StopT[];
  value: number;
};

/**
 * Given slider value (in consumer's units), and the array of slider stops,
 * it calculates slider handle's position as a fraction of slider's width.
 *
 * BEWARE: It assumes `stops` are ordered in a monotonic, increasing order;
 * it does not perform an exhaustive validation of `stops` on each call, but
 * it will throw if it finds during the calculation, that this assumption does
 * not hold.
 */
function calcPos(value: number, stops: StopT[]): number {
  let left = stops[0]?.[0];
  if (left === undefined) throw Error('Internal error');

  if (value <= left) return 0;

  // The step between stops, as a fraction of the slider's width.
  const step = 1 / (stops.length - 1);

  for (let i = 1; i < stops.length; ++i) {
    const right = stops[i]?.[0];
    if (right === undefined) throw Error('Internal error');
    if (left >= right) throw Error('Invalid stops');

    if (value === right) return i * step;

    if (value < right) {
      // Fractional position of the value between two nearest stops on its left
      // and right.
      const f = (value - left) / (right - left);

      return (i + f - 1) * step;
    }

    left = right;
  }

  return 1;
}

/**
 * Given the current slider position (as [0, 1] number), and the array of its
 * stops, it calculates the current slider value (in stops' units).
 * @param pos Current slider position, as [0, 1] number (i.e. the percentage
 *  of the total slider width, where its handle is currently placed).
 * @param stops Array of slider stops.
 * @return Slider value, in the same units used by `stops`.
 */
export function calcValue(pos: number, stops: StopT[]): number {
  // The step between stops, as a fraction of the total slider width.
  const step = 1 / (stops.length - 1);

  // "Fractional index" of the stop at the current slider position.
  let idx = pos / step;

  const stopAtIdx = stops[idx];
  if (!stopAtIdx) throw Error('Internal error');

  if (Number.isInteger(idx)) return stopAtIdx[0];

  // Now it is the index of the stop just to the left of the current slider position.
  idx = Math.floor(idx);

  // Fractional position of the slider within the segment between the two
  // nearest stops on its left and right.
  const f = (pos - idx * step) / step;

  const stopAfterIdx = stops[idx + 1];
  if (!stopAfterIdx) throw Error('Internal error');

  return (1 - f) * stopAtIdx[0] + f * stopAfterIdx[0];
}

/**
 * Given the current slider position (as [0, 1] number), and the array of its
 * stops, it snaps the position to its nearest stop, and returns that stop's
 * value.
 */
function snapValue(pos: number, stops: StopT[]): number {
  const step = 1 / (stops.length - 1);
  const idx = Math.min(
    stops.length - 1,
    Math.max(0, Math.round(pos / step)),
  );

  const res = stops[idx]?.[0];
  if (res === undefined) throw Error('Internal error');
  return res;
}

const SliderStepInput: FunctionComponent<PropsT> = ({
  light,
  onChange,
  stops,
  value,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState(false);

  const labels: ReactNode[] = [];
  for (const stop of stops) {
    labels.push(
      <div key={stop[0]}>
        {stop[1]}
      </div>,
    );
  }

  const pos = useMemo(() => calcPos(value, stops), [value, stops]);

  return (
    <div styleName={light ? 'container light' : 'container'}>
      <div styleName="slider">
        <div ref={sliderRef}>
          <div
            onPointerDown={(e) => {
              const el = e.target as Element;
              el.setPointerCapture(e.pointerId);
              setActive(true);
            }}
            onPointerMove={active ? (e) => {
              if (!sliderRef.current) throw Error('Missing reference');
              const rect = sliderRef.current.getBoundingClientRect();
              let newPos = (e.clientX - rect.left) / rect.width;
              if (newPos < 0) newPos = 0;
              else if (newPos > 1) newPos = 1;
              onChange(snapValue(newPos, stops));
            } : undefined}
            onPointerUp={() => {
              setActive(false);
            }}
            style={{
              cursor: active ? 'grabbing' : 'grab',
              left: `${100 * pos}%`,
            }}
            styleName="handle"
          />
        </div>
      </div>
      <div styleName="labels">
        {labels}
      </div>
    </div>
  );
};

export default SliderStepInput;
